import { defineStore } from 'pinia';
import type { IVdpTableState } from '../types/store-states.types';
import DatasetRowModel from '../models/dataset-row.model';
import { useConfigStore } from './config.store';
import DesignAtomsService from '../services/design-atoms.service';
import { useEditorStore } from './editor.store';


export const useVdpTableStore = defineStore('au-vdp-table', {
  state: () => ({
    loadedData: null,
    dataset: null,
    loadedSurfacesData: null,
    surfacesData: null,
  } as IVdpTableState),

  getters: {
    getDataset: (state) => {
      const dataset = state.dataset?.map((dt) => {
        dt.columns = dt.columns.sort((a, b) => {
          const numA = parseInt(a.match(/\d+/)?.[0] || '0', 10);
          const numB = parseInt(b.match(/\d+/)?.[0] || '0', 10);
          return numA - numB;
        });
        return dt;
      });
      return dataset || null;
    },
    getSurfacesData: (state) => state.surfacesData,
  },

  actions: {
    setLoadedData(data: IVdpTableState['loadedData']) {
      if (!data) return;
      this.loadedData = data;
      this.processDataset();
    },

    processDataset() {
      if (!this.loadedData) return;
      this.dataset = this.loadedData.map((dataset) => {
        const datasetRow = new DatasetRowModel(dataset.columns, dataset.rows);
        return datasetRow.value;
      });
    },

    setLoadedSurfacesData(data: IVdpTableState['loadedSurfacesData']) {
      if (!data) return;
      this.loadedSurfacesData = data;
      this.processSurfacesData();
    },

    processSurfacesData() {
      if (!this.loadedSurfacesData) return;
      this.surfacesData = this.loadedSurfacesData;
    },

    async fetchVdpData() {
      const configStore = useConfigStore();

      const stateId = (Array.isArray(configStore.getProductDefinition)) ? configStore.getProductDefinition[0] : configStore.getProductDefinition;
      const apigateway = configStore.getApiGatewayUrl;
      const user = configStore.getUser;
      const tenantId = configStore.getTenantId;
      if (
        !stateId ||
        !apigateway ||
        !user ||
        !tenantId
      ) {
        console.warn('Attempt to obtain VDP data to state failed. Missing required parameters');
        return null;
      }
      const options: {
        apigateway: string;
        stateId: string;
        token: string;
        tenantId: string;
        userId: string;
      } = {
        apigateway: apigateway,
        stateId: stateId,
        token: user.token,
        tenantId: tenantId,
        userId: user.id,
      };
      const designAtomsService = new DesignAtomsService(options);
      return await designAtomsService.getVdpData();
    },

    async putVdpInState() {
      const editorStore = useEditorStore();
      const configStore = useConfigStore();

      const dataset = editorStore.getDataset;
      const surfacesData = this.getSurfacesData;
      const apigateway = configStore.getApiGatewayUrl;
      const user = configStore.getUser;
      const tenantId = configStore.getTenantId;
      const stateId = editorStore.getAdaptiveStateId;
      if (
        !stateId ||
        !dataset ||
        !surfacesData ||
        !apigateway ||
        !user ||
        !tenantId
      ) {
        console.warn('Attempt to save VDP data to state failed. Missing required parameters', { stateId, dataset, surfacesData, apigateway, user, tenantId });
        return;
      }
      const options: {
        apigateway: string;
        stateId: string;
        token: string;
        tenantId: string;
        userId: string;
      } = {
        apigateway: apigateway,
        stateId: stateId,
        token: user.token,
        tenantId: tenantId,
        userId: user.id,
      };
      const designAtomsService = new DesignAtomsService(options);
      return designAtomsService.putVdp(dataset, surfacesData);
    }
  },
});